interface UseTimepadRegistrationOptions {
  timepadId: string | null | undefined;
  timepadScript: string | null | undefined;
}

interface ParsedTimepadScript {
  attributes: Record<string, string>;
  inlineConfig: string;
  src: string;
}

interface TimepadTriggerBinding {
  attrs: Record<string, string>;
  selector: string;
  tagName: 'a' | 'button';
}

const DEFAULT_TIMEPAD_WIDGET_SRC =
  'https://timepad.ru/js/tpwf/loader/min/loader.js';

const normalizeTimepadEventId = (rawTimepadId: string) => {
  const trimmedTimepadId = rawTimepadId.trim();

  if (!trimmedTimepadId) {
    return '';
  }

  if (/^\d+$/.test(trimmedTimepadId)) {
    return trimmedTimepadId;
  }

  const registerIdMatch = trimmedTimepadId.match(
    /timepad_twf_register_(\d+)$/i,
  );

  if (registerIdMatch?.[1]) {
    return registerIdMatch[1];
  }

  const trailingDigitsMatch = trimmedTimepadId.match(/(\d+)$/);

  if (trailingDigitsMatch?.[1]) {
    return trailingDigitsMatch[1];
  }

  return trimmedTimepadId;
};

const parseTimepadScript = (scriptCode: string): ParsedTimepadScript => {
  const trimmedScriptCode = scriptCode.trim();

  if (!trimmedScriptCode) {
    return {
      attributes: {},
      inlineConfig: '',
      src: DEFAULT_TIMEPAD_WIDGET_SRC,
    };
  }

  if (!import.meta.client) {
    return {
      attributes: {},
      inlineConfig: trimmedScriptCode,
      src: DEFAULT_TIMEPAD_WIDGET_SRC,
    };
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(
    trimmedScriptCode,
    'text/html',
  );
  const scriptElement = documentFragment.querySelector('script');

  if (!scriptElement) {
    return {
      attributes: {},
      inlineConfig: trimmedScriptCode,
      src: DEFAULT_TIMEPAD_WIDGET_SRC,
    };
  }

  const attributes = Object.fromEntries(
    Array.from(scriptElement.attributes).map((attribute) => [
      attribute.name,
      attribute.value,
    ]),
  );

  return {
    attributes,
    inlineConfig: scriptElement.textContent?.trim() ?? '',
    src:
      scriptElement.getAttribute('src')?.trim() || DEFAULT_TIMEPAD_WIDGET_SRC,
  };
};

const normalizeInlineConfigExpression = (inlineConfig: string) => {
  const trimmedInlineConfig = inlineConfig.trim().replace(/;+\s*$/, '');

  if (!trimmedInlineConfig) {
    return '({})';
  }

  if (
    trimmedInlineConfig.startsWith('{') &&
    trimmedInlineConfig.endsWith('}')
  ) {
    return `(${trimmedInlineConfig})`;
  }

  return trimmedInlineConfig;
};

const extractTriggerSelector = (inlineConfig: string) => {
  const triggerSelectorMatch = inlineConfig.match(
    /["']triggerSelector["']\s*:\s*["']([^"']+)["']/,
  );

  return triggerSelectorMatch?.[1]?.trim() || '';
};

const createFallbackTriggerBinding = (): TimepadTriggerBinding => ({
  selector: '[data-timepad-trigger="registration"]',
  tagName: 'button',
  attrs: {
    'data-timepad-trigger': 'registration',
    type: 'button',
  },
});

const resolveTriggerBinding = (
  triggerSelector: string,
): TimepadTriggerBinding => {
  const normalizedTriggerSelector = triggerSelector.trim();

  if (!normalizedTriggerSelector) {
    return createFallbackTriggerBinding();
  }

  if (normalizedTriggerSelector.startsWith('#')) {
    return {
      selector: normalizedTriggerSelector,
      tagName: 'button',
      attrs: {
        id: normalizedTriggerSelector.slice(1),
        type: 'button',
      },
    };
  }

  const anchorHrefSelectorMatch = normalizedTriggerSelector.match(
    /^a\[href=(["'])(.+)\1\]$/,
  );

  if (anchorHrefSelectorMatch?.[2]) {
    return {
      selector: normalizedTriggerSelector,
      tagName: 'a',
      attrs: {
        href: anchorHrefSelectorMatch[2],
      },
    };
  }

  const dataAttributeSelectorMatch = normalizedTriggerSelector.match(
    /^\[([^\]=]+)=(["']?)([^"'\]]+)\2\]$/,
  );

  if (dataAttributeSelectorMatch?.[1] && dataAttributeSelectorMatch[3]) {
    return {
      selector: normalizedTriggerSelector,
      tagName: 'button',
      attrs: {
        [dataAttributeSelectorMatch[1]]: dataAttributeSelectorMatch[3],
        type: 'button',
      },
    };
  }

  if (normalizedTriggerSelector.startsWith('.')) {
    return {
      selector: normalizedTriggerSelector,
      tagName: 'button',
      attrs: {
        class: normalizedTriggerSelector.slice(1),
        type: 'button',
      },
    };
  }

  return createFallbackTriggerBinding();
};

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

const buildTriggerMarkup = (binding: TimepadTriggerBinding) => {
  const attributesMarkup = Object.entries(binding.attrs)
    .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
    .join(' ');

  return `<${binding.tagName} ${attributesMarkup}>Open</${binding.tagName}>`;
};

const buildTimepadConfig = (
  inlineConfig: string,
  timepadId: string,
  triggerSelector: string,
) => {
  const originalConfigExpression =
    normalizeInlineConfigExpression(inlineConfig);

  return `(function () {
    var creOriginalConfig = {};

    try {
      creOriginalConfig = ${originalConfigExpression} || {};
    } catch (error) {
      console.error('Failed to parse Timepad config', error);
    }

    var crePopupConfig = creOriginalConfig.popup || {};

    return Object.assign({}, creOriginalConfig, {
      event: Object.assign({}, creOriginalConfig.event || {}, {
        id: ${JSON.stringify(timepadId)}
      }),
      bindEvents: Object.assign({}, creOriginalConfig.bindEvents || {}, {
        preReady: "handleTWFpreReady",
        switchedToNewRenderTarget: "handleTWFswitchedToNewRenderTarget",
        preRepaint: "handleTWFpreRepaint",
        postRepaint: "handleTWFpostRepaint"
      }),
      debug: true,
      hidePreloading: true,
      display: 'popup',
      popup: Object.assign({}, crePopupConfig, {
        triggerSelector: ${JSON.stringify(triggerSelector)}
      })
    });
  })();`;
};

const buildIframeSrcdoc = (
  parsedScript: ParsedTimepadScript,
  timepadId: string,
) => {
  const triggerBinding = resolveTriggerBinding(
    extractTriggerSelector(parsedScript.inlineConfig),
  );

  const widgetAttributes = {
    ...parsedScript.attributes,
    'data-timepad-widget-v2':
      parsedScript.attributes['data-timepad-widget-v2'] || 'event_register',
    src: parsedScript.src || DEFAULT_TIMEPAD_WIDGET_SRC,
  };

  const scriptAttributesMarkup = Object.entries(widgetAttributes)
    .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
    .join(' ');

  const triggerMarkup = buildTriggerMarkup(triggerBinding);
  const config = buildTimepadConfig(
    parsedScript.inlineConfig,
    timepadId,
    triggerBinding.selector,
  );

  return `<!DOCTYPE html>
<html lang="ru">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
      html, body {
        margin: 0;
        min-height: 100%;
        background: transparent;
      }

      body {
        font-family: sans-serif;
      }

      .timepad-trigger {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    </style>
  </head>
  <body>
    <div class="timepad-trigger">${triggerMarkup}</div>
    <script>
      window.__creTimepadState = {
        seenActiveRoute: false,
        closeNotified: false,
        openNotified: false,
        popupSeenInDom: false,
        targetNodesSeen: false,
      };

      window.__creSanitizePayload = function(value, seen) {
        if (value == null) {
          return value;
        }

        if (typeof value === 'function') {
          return '[Function]';
        }

        if (typeof Node !== 'undefined' && value instanceof Node) {
          return '[DOMNode]';
        }

        if (typeof value !== 'object') {
          return value;
        }

        var visited = seen || new WeakSet();

        if (visited.has(value)) {
          return '[Circular]';
        }

        visited.add(value);

        if (Array.isArray(value)) {
          return value.map(function(item) {
            return window.__creSanitizePayload(item, visited);
          });
        }

        var nextValue = {};

        Object.keys(value).forEach(function(key) {
          nextValue[key] = window.__creSanitizePayload(value[key], visited);
        });

        return nextValue;
      };

      window.__creNotifyParent = function(type, payload) {
        if (type === 'timepad-open') {
          if (window.__creTimepadState.openNotified) {
            return;
          }

          window.__creTimepadState.openNotified = true;
        }

        if (type === 'timepad-close') {
          if (window.__creTimepadState.closeNotified) {
            return;
          }

          window.__creTimepadState.closeNotified = true;
        }

        window.parent.postMessage(
          {
            source: 'cre-timepad',
            type: type,
            payload: window.__creSanitizePayload(payload || null),
          },
          '*',
        );
      };

      window.__creExtractRoute = function(params) {
        if (!params || typeof params !== 'object') {
          return '';
        }

        return params.route || params.name || params.targetRoute || '';
      };

      window.__creIsNodeVisible = function(node) {
        if (!(node instanceof Element)) {
          return false;
        }

        var style = window.getComputedStyle(node);

        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          style.opacity === '0'
        ) {
          return false;
        }

        var rect = node.getBoundingClientRect();

        return rect.width > 0 && rect.height > 0;
      };

      window.__creGetPopupTargets = function() {
        return {
          tint: document.querySelector('.twf-popup-tint'),
          container: document.querySelector('.twf-popup-container'),
        };
      };

      window.__creHasVisiblePopupTargets = function() {
        var popupTargets = window.__creGetPopupTargets();

        return (
          window.__creIsNodeVisible(popupTargets.tint) ||
          window.__creIsNodeVisible(popupTargets.container)
        );
      };

      window.__creHandleDomStateChange = function(reason) {
        var hasVisibleTargets = window.__creHasVisiblePopupTargets();

        if (hasVisibleTargets) {
          window.__creNotifyParent('timepad-open', {
            reason: reason,
          });
          window.__creTimepadState.targetNodesSeen = true;
          window.__creTimepadState.popupSeenInDom = true;
          return;
        }

        if (window.__creTimepadState.targetNodesSeen) {
          window.__creNotifyParent('timepad-close', {
            reason: reason,
          });
        }
      };

      window.__creObservePopupLifecycle = function() {
        var observer = new MutationObserver(function() {
          window.__creHandleDomStateChange('dom-popup-removed');
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        document.addEventListener(
          'click',
          function(event) {
            var target = event.target;

            if (!(target instanceof Element)) {
              return;
            }

            var closeCandidate = target.closest(
              '[aria-label*="close" i], [aria-label*="закры" i], [class*="close"], [class*="overlay"], [data-close], [data-dismiss]',
            );

            if (closeCandidate) {
              window.setTimeout(function() {
                window.__creHandleDomStateChange('close-click');
              }, 300);
            }
          },
          true,
        );

        window.setTimeout(function() {
          window.__creHandleDomStateChange('initial-dom-check');
        }, 500);
      };

      window.handleTWFpreReady = function(params) {
        window.__creNotifyParent('timepad-debug', {
          event: 'preReady',
          params: params || null,
        });
      };

      window.handleTWFswitchedToNewRenderTarget = function(params) {
        window.__creNotifyParent('timepad-debug', {
          event: 'switchedToNewRenderTarget',
          params: params || null,
        });
      };

      window.handleTWFpreRepaint = function(params) {
        window.__creNotifyParent('timepad-debug', {
          event: 'preRepaint',
          params: params || null,
        });
      };

      window.handleTWFpostRepaint = function(params) {
        var route = window.__creExtractRoute(params);

        window.__creNotifyParent('timepad-debug', {
          event: 'postRepaint',
          params: params || null,
          route: route || null,
        });

        if (route && route !== 'button') {
          window.__creTimepadState.seenActiveRoute = true;
        }

        if (window.__creTimepadState.seenActiveRoute && route === 'button') {
          window.__creNotifyParent('timepad-close', {
            reason: 'route-returned-to-button',
            params: params || null,
          });
        }
      };
    </script>
    <script ${scriptAttributesMarkup}>
      ${config}
    </script>
    <script>
      window.addEventListener('load', function () {
        window.__creObservePopupLifecycle();

        window.setTimeout(function () {
          var trigger = document.querySelector(${JSON.stringify(triggerBinding.selector)});
          if (trigger) {
            trigger.click();
          }
        }, 250);
      });
    </script>
  </body>
</html>`;
};

export const useTimepadRegistration = (
  options: MaybeRefOrGetter<UseTimepadRegistrationOptions>,
) => {
  const resolvedOptions = computed(() => toValue(options));

  const hasTimepadRegistration = computed(
    () =>
      Boolean(resolvedOptions.value.timepadId) &&
      Boolean(resolvedOptions.value.timepadScript),
  );

  const normalizedTimepadId = computed(() =>
    resolvedOptions.value.timepadId
      ? normalizeTimepadEventId(resolvedOptions.value.timepadId)
      : '',
  );

  const parsedScript = computed(() =>
    resolvedOptions.value.timepadScript
      ? parseTimepadScript(resolvedOptions.value.timepadScript)
      : null,
  );

  const iframeSrcdoc = computed(() => {
    if (
      !hasTimepadRegistration.value ||
      !parsedScript.value ||
      !normalizedTimepadId.value
    ) {
      return '';
    }

    return buildIframeSrcdoc(parsedScript.value, normalizedTimepadId.value);
  });

  return {
    hasTimepadRegistration,
    iframeSrcdoc,
  };
};
